import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ToasterService } from '@arianeeprivate/wallet-shared-components';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ethers } from 'ethers';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { Button } from '../../../components/redesign/button/models/button.model';
import { ArianeeService } from '../../../providers/arianee-service/arianee.service';
import { BackupService } from '../../../providers/backup-service/backup.service';
import { EventLoggerService } from '../../../providers/event-logger/event-logger-service';
import { FeatureFlipService } from '../../../providers/feature-flip-service/feature-flip.service';
import { SentryErrorHandler } from '../../../providers/sentry-service/sentry-service';
import { UserService } from '../../../providers/user-service/user.service';

@Component({
	selector: 'app-cloud-import',
	templateUrl: './cloud-import.page.html',
	styleUrls: ['./cloud-import.page.scss'],
})
export class CloudImportPage implements AfterViewInit, OnInit, OnDestroy {
	@ViewChild('header', {
		read: ElementRef,
		static: false,
	})
	header: ElementRef<HTMLElement>;

	public headerHeight: number = 0;

	public importLoading = false;

	public backups: { date: Date; id: string }[] = [];
	public selectedBackupId: string = '';
	cloudImportBtn: Button;

	subscription = Subscription.EMPTY;
	darkMode = false;

	constructor(
		private eventLogger: EventLoggerService,
		private arianeeService: ArianeeService,
		private platform: Platform,
		private router: Router,
		private backupService: BackupService,
		private toasterService: ToasterService,
		private sentryService: SentryErrorHandler,
		private userService: UserService,
		private translate: TranslateService,
		private ffService: FeatureFlipService,
	) {}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	async ngOnInit() {
		// Subscribe to Dark Mode config
		this.subscription = this.ffService
			.$isFeatureFlip('darkMode')
			.subscribe((active: boolean) => {
				this.darkMode = !!active;
			});

		await this.setBackups();
		this.cloudImportBtn = {
			title: {
				text: this.translate.instant('Import.import'),
			},
			buttonType: 'primary',
			loading: this.importLoading,
			disabled: this.importLoading || this.backups.length === 0,
		};
	}

	ngAfterViewInit() {
		this.log('screen', 'cloud-import');
		this.setHeaderHeight();
	}

	private setHeaderHeight() {
		this.headerHeight = (
			this.header.nativeElement.firstElementChild as HTMLElement
		).offsetHeight;

		// Retry if css not yet loaded
		if (this.headerHeight === 0)
			setTimeout(this.ngAfterViewInit.bind(this), 10);
	}

	private async setBackups() {
		this.backups = await this.backupService.getBackupsId();
		this.selectedBackupId = this.backups.length > 0 ? this.backups[0].id : '';
	}

	public get cloudProvider(): string {
		if (this.platform.is('ios')) {
			return 'iCloud';
		}

		return 'Google Drive';
	}

	private async log(type: 'screen' | 'event', name: string) {
		const address = await this.arianeeService.$address
			.pipe(take(1))
			.toPromise();

		if (type === 'screen') {
			this.eventLogger.logScreen(name, {
				publicKey: address,
			});
		} else {
			await this.eventLogger.logEvent(name, {
				publicKey: address,
			});
		}
	}

	public goBack() {
		this.router.navigate(['/tab/setting']);
		this.log('event', 'cloud-import__go-to-settings');
	}

	public onBackupClick(backupId: string) {
		this.selectedBackupId = backupId;
	}

	public async cloudImport() {
		if (this.importLoading) return;

		try {
			this.importLoading = true;

			this.log('event', 'cloud-import__import-start');

			const shouldContinue =
				await this.backupService.alertIfImportBackupNotSafe();

			if (shouldContinue) {
				const mnemonic = await this.backupService.getMnemonic(
					this.selectedBackupId,
				);
				const isValidMnemonic = ethers.utils.isValidMnemonic(mnemonic);

				if (!isValidMnemonic) {
					this.log('event', 'cloud-import__import-invalid-mnemonic');
					this.sentryService.sentry.captureMessage(
						'backup_import_modal_invalid_mnemonic',
						{
							tags: {
								invalidMnemonic: this.selectedBackupId,
							},
						},
					);

					await this.toasterService.showRedesigned({
						title: 'Backup.corruptedBackup',
						position: 'top',
						duration: 8000,
						color: 'danger',
						icon: 'cancel',
					});
				} else {
					await this.arianeeService
						.switchWallet(mnemonic)
						.pipe(take(1))
						.toPromise();
					await this.backupService.checkBackup();

					await this.log('event', 'cloud-import__import-success');
					this.toasterService.showRedesigned({
						title: 'Import.cloudImportSuccessful',
						position: 'top',
						duration: 4000,
						color: 'success',
						icon: 'check',
					});

					await this.router.navigate(['tab/brand-list']);
					window.location.reload();
				}
			}
			this.userService.setHasBackup(true);
		} catch (e) {
			console.error('Error while performing cloud backup', e);
			this.toasterService.showRedesigned({
				title: 'Import.cloudImportFailed',
				position: 'top',
				duration: 4000,
				color: 'danger',
				icon: 'cancel',
			});
			this.log('event', 'cloud-import__import-error');
		} finally {
			this.importLoading = false;
		}
	}
}
